import { React, useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleInfo, faTrashCan, faCloud, faPlus, faPen, faCheck, faXmark } from '@fortawesome/free-solid-svg-icons'
import { browserSupportsWebAuthn, startRegistration } from '@simplewebauthn/browser'

import CTAButton from '../CTAButton/CTAButton.js'
import Skeleton from '../Skeleton/Skeleton.js'

import { URL_MAINAPI, API_TOKEN, formatDateWithTime, describePasskeyError } from '../../main.js'

import '../../main.css'
import './PasskeyManager.css'
import './MPasskeyManager.css'

import Cookies from 'universal-cookie'


export default function PasskeyManager() {

    const cookies = new Cookies()
    let cur_authtoken = cookies.get('authtoken')

    const headers = {
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'APITOKEN': API_TOKEN,
        'authtoken': cur_authtoken
    }

    var passkeySupported = browserSupportsWebAuthn()
    var [passkeys, setPasskeys] = useState([])
    var [loaded, setLoaded] = useState(false)
    var [status, setStatus] = useState("")
    var [message, setMessage] = useState("")
    var [editingId, setEditingId] = useState(null)
    var [editName, setEditName] = useState("")

    useEffect(() => {
        getPasskeys()
    }, [])

    function showResult(newStatus, newMessage) {
        setStatus(newStatus)
        setMessage(newMessage)
    }

    async function getPasskeys() {
        try {
            const res = await fetch(URL_MAINAPI + "/auth/passkey/", { method: "GET", headers: headers })
            const data = await res.json()
            if (res.status === 200) {
                setPasskeys(data)
            } else {
                showResult("error", data.error)
            }
        } catch (err) {
            showResult("error", String(err))
        }
        setLoaded(true)
    }

    async function doAddPasskey() {
        showResult("loading", "")
        try {
            const optionsRes = await fetch(URL_MAINAPI + "/auth/passkey/register-options", { method: "POST", headers: headers })
            const optionsData = await optionsRes.json()
            if (optionsRes.status !== 200) {
                showResult("error", optionsData.error)
                return
            }

            const attestation = await startRegistration({ optionsJSON: optionsData })

            const registerRes = await fetch(URL_MAINAPI + "/auth/passkey/register", {
                method: "POST",
                headers: headers,
                body: JSON.stringify({ response: attestation }),
            })
            const registerData = await registerRes.json()
            if (registerRes.status === 200) {
                showResult("done", "Passkey „" + registerData.passkey.name + "“ wurde hinzugefügt. Du kannst dich ab jetzt damit anmelden.")
                getPasskeys()
            } else {
                showResult("error", registerData.error)
            }
        } catch (err) {
            const errorMessage = describePasskeyError(err)
            showResult(errorMessage ? "error" : "", errorMessage || "")
        }
    }

    function startRename(passkey) {
        setEditingId(passkey._id)
        setEditName(passkey.name)
    }

    async function doRenamePasskey(passkey) {
        showResult("loading", "")
        try {
            const res = await fetch(URL_MAINAPI + "/auth/passkey/" + passkey._id, {
                method: "PATCH",
                headers: headers,
                body: JSON.stringify({ name: editName }),
            })
            const data = await res.json()
            if (res.status === 200) {
                setEditingId(null)
                showResult("done", "Passkey wurde in „" + data.passkey.name + "“ umbenannt.")
                getPasskeys()
            } else {
                showResult("error", data.error)
            }
        } catch (err) {
            showResult("error", String(err))
        }
    }

    async function doDeletePasskey(passkey) {
        if (!window.confirm("Passkey „" + passkey.name + "“ wirklich entfernen? Lösche ihn danach auch in deinem Passwortmanager bzw. Schlüsselbund.")) return

        showResult("loading", "")
        try {
            const res = await fetch(URL_MAINAPI + "/auth/passkey/" + passkey._id, { method: "DELETE", headers: headers })
            const data = await res.json()
            if (res.status === 200) {
                showResult("done", "Passkey „" + passkey.name + "“ wurde entfernt.")
                getPasskeys()
            } else {
                showResult("error", data.error)
            }
        } catch (err) {
            showResult("error", String(err))
        }
    }

    return (
        <div className='passkeys'>
            {(!passkeySupported) ? (
                <div className='status-banner status-banner--info' role="status">
                    <FontAwesomeIcon icon={faCircleInfo} />
                    <span>Dein Browser unterstützt keine Passkeys. Nutze einen aktuellen Browser wie Safari, Chrome, Edge oder Firefox.</span>
                </div>
            ) : null}

            {(!loaded) ? <Skeleton height={56} /> : null}

            {(loaded && passkeys.length === 0) ? (
                <p className='passkeys-empty'>Du hast noch keinen Passkey eingerichtet.</p>
            ) : null}

            {(passkeys.length > 0) ? (
                <ul className='passkeys-list'>
                    {passkeys.map(function (passkey) {
                        return (
                            <li key={passkey._id} className='passkeys-item'>
                                {(editingId === passkey._id) ? (
                                    <form className='passkeys-rename' onSubmit={(e) => { e.preventDefault(); doRenamePasskey(passkey) }}>
                                        <div className='field passkeys-rename-field'>
                                            <input
                                                type="text"
                                                maxLength="40"
                                                autoFocus
                                                aria-label="Name des Passkeys"
                                                value={editName}
                                                onChange={(e) => setEditName(e.target.value)}
                                            />
                                        </div>
                                        <button type="submit" className='btn btn-secondary btn-sm'>
                                            <FontAwesomeIcon icon={faCheck} /> Speichern
                                        </button>
                                        <button type="button" className='btn btn-quiet btn-sm' onClick={() => setEditingId(null)}>
                                            <FontAwesomeIcon icon={faXmark} /> Abbrechen
                                        </button>
                                    </form>
                                ) : (
                                    <>
                                        <div className='passkeys-item-info'>
                                            <div className='passkeys-item-title'>
                                                <b>{passkey.name}</b>
                                                {(passkey.backedUp) ? (
                                                    <span className='badge badge-info'><FontAwesomeIcon icon={faCloud} /> Synchronisiert</span>
                                                ) : null}
                                            </div>
                                            <div className='passkeys-item-meta'>
                                                <span>Hinzugefügt: {formatDateWithTime(new Date(passkey.dates.created))}</span>
                                                {(passkey.dates.lastused) ? (
                                                    <span>Zuletzt genutzt: {formatDateWithTime(new Date(passkey.dates.lastused))}</span>
                                                ) : null}
                                            </div>
                                        </div>
                                        <div className='passkeys-item-actions'>
                                            <button
                                                type="button"
                                                className='btn btn-quiet btn-sm'
                                                onClick={() => startRename(passkey)}
                                                aria-label={"Passkey " + passkey.name + " umbenennen"}
                                            >
                                                <FontAwesomeIcon icon={faPen} /> Umbenennen
                                            </button>
                                            <button
                                                type="button"
                                                className='btn btn-quiet btn-sm passkeys-delete'
                                                onClick={() => doDeletePasskey(passkey)}
                                                aria-label={"Passkey " + passkey.name + " entfernen"}
                                            >
                                                <FontAwesomeIcon icon={faTrashCan} /> Entfernen
                                            </button>
                                        </div>
                                    </>
                                )}
                            </li>
                        )
                    })}
                </ul>
            ) : null}

            {(passkeySupported) ? (
                <div style={{ marginTop: '18px' }}>
                    <CTAButton
                        variant="primary"
                        hideArrow={true}
                        disabled={status === "loading"}
                        title={<><FontAwesomeIcon icon={faPlus} /> Passkey hinzufügen</>}
                        onclick={() => doAddPasskey()}
                    />
                </div>
            ) : null}

            {(status === "loading") ? (
                <div className='status-banner status-banner--info' role="status">
                    <FontAwesomeIcon icon={faCircleInfo} /> <span>Lädt... Bitte folge dem Dialog deines Geräts bzw. Passwortmanagers.</span>
                </div>
            ) : null}
            {(status === "done") ? (
                <div className='status-banner status-banner--success' role="status">
                    <FontAwesomeIcon icon={faCircleInfo} /> <span>{message}</span>
                </div>
            ) : null}
            {(status === "error") ? (
                <div className='status-banner status-banner--error' role="alert">
                    <FontAwesomeIcon icon={faCircleInfo} /> <span>Es gab einen Fehler.<br />Fehler: {message}</span>
                </div>
            ) : null}

            <p className='passkeys-hint'>
                Ein Passkey ersetzt dein Passwort: Der geheime Schlüssel bleibt auf deinem Gerät bzw. in deinem Passwortmanager, Helferzeit speichert nur den öffentlichen Teil.
            </p>
        </div>
    )
}
